/**
 * DrawerOverlay — a slide-in panel that overlays the parent container.
 *
 * Compound component with two parts:
 *
 * - `DrawerOverlay` — root wrapper that establishes the positioning context.
 *   Renders a `relative overflow-hidden` container and provides a portal
 *   target for any nested `DrawerOverlayContent`.
 *
 * - `DrawerOverlayContent` — the slide-in panel itself. Can be rendered
 *   anywhere inside a `DrawerOverlay` tree — it portals back up to the
 *   root container. Throws if rendered outside a `DrawerOverlay`.
 *
 * Designed for "nested context" situations where a dialog is already open and
 * a second layer of temporary UI is needed (e.g. inline entity creation).
 */
import { createContext, useContext, useRef } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/frontend/components/ui/button'
import { cn } from '@/frontend/lib/utils'

const DEFAULT_GUTTER_LEFT = 48
const DEFAULT_GUTTER_Y = 8

const DrawerOverlayContext = createContext<React.RefObject<HTMLDivElement | null> | null>(null)

function useDrawerOverlayRoot() {
  const ctx = useContext(DrawerOverlayContext)
  if (!ctx) {
    throw new Error('DrawerOverlayContent must be rendered inside a DrawerOverlay')
  }
  return ctx
}

interface DrawerOverlayProps {
  children: React.ReactNode
  className?: string
}

/** Root wrapper — establishes the positioning context and portal target. */
function DrawerOverlay({ children, className }: DrawerOverlayProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  return (
    <DrawerOverlayContext.Provider value={ref}>
      <div ref={ref} className={cn('relative overflow-hidden', className)}>
        {children}
      </div>
    </DrawerOverlayContext.Provider>
  )
}

interface DrawerOverlayContentProps {
  open: boolean
  title: string
  onCancel: () => void
  onConfirm: () => void
  /** Label for the confirm button. Defaults to `'Confirm'`. */
  confirmLabel?: string
  /** Disables the confirm button (e.g. while submitting). */
  confirmDisabled?: boolean
  /** Left gutter in px — how much parent content peeks through. Defaults to 48. */
  gutterLeft?: number
  /** Top/bottom gutter in px. Defaults to 8. */
  gutterY?: number
  children: React.ReactNode
  className?: string
}

/** Slide-in panel — portals into the nearest DrawerOverlay root. */
function DrawerOverlayContent({
  open,
  title,
  onCancel,
  onConfirm,
  confirmLabel = 'Confirm',
  confirmDisabled,
  gutterLeft = DEFAULT_GUTTER_LEFT,
  gutterY = DEFAULT_GUTTER_Y,
  children,
  className,
}: DrawerOverlayContentProps) {
  const rootRef = useDrawerOverlayRoot()

  const panel = (
    <>
      {/* Inert overlay — blocks pointer events on parent content while drawer is open */}
      {open && <div aria-hidden className="absolute inset-0 z-10" />}

      <section
        aria-label={title}
        className={cn(
          'absolute right-0 z-20 flex flex-col bg-card transition-transform duration-200 ease-out',
          'rounded-l-xl border-l border-y',
          open
            ? 'translate-x-0 shadow-[-12px_0_32px_-2px_rgba(0,0,0,0.2),0_-2px_6px_-2px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.15)]'
            : 'translate-x-full shadow-none',
          className
        )}
        style={{ left: gutterLeft, top: gutterY, bottom: gutterY }}
      >
        {/* Header */}
        <div className="border-b px-4 py-3">
          <span className="font-semibold">{title}</span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </section>
    </>
  )

  if (!rootRef.current) return null
  return createPortal(panel, rootRef.current)
}

export { DrawerOverlay, DrawerOverlayContent }
